import React, { createContext, useState, useEffect, useCallback } from 'react';
import axios from '../axios-config';
import LoadingAcorn from '../components/LoadingAcorn';

interface LoadingContextValue {
  isLoading: boolean;
  startLoading: () => void;
  stopLoading: () => void;
}

export const LoadingContext = createContext<LoadingContextValue>({
  isLoading: false,
  startLoading: () => {},
  stopLoading: () => {},
});

export const LoadingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [count, setCount] = useState(0);

  const startLoading = useCallback(() => setCount(c => c + 1), []);
  const stopLoading = useCallback(() => setCount(c => Math.max(0, c - 1)), []);

  useEffect(() => {
    const req = axios.interceptors.request.use(
      config => {
        const shouldTrack = !config.skipLoading;
        config.__didStartLoading = shouldTrack;

        if (shouldTrack) {
          startLoading();
        }
        return config;
      },
      error => {
        stopLoading();
        return Promise.reject(error);
      }
    );

    const res = axios.interceptors.response.use(
      response => {
        const didStart = response?.config?.__didStartLoading;
        if (didStart) {
          stopLoading();
        }
        return response;
      },
      error => {
        const didStart = error?.config?.__didStartLoading;
        if (didStart) {
          stopLoading();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(req);
      axios.interceptors.response.eject(res);
    };
  }, [startLoading, stopLoading]);

  return (
    <LoadingContext.Provider value={{ isLoading: count > 0, startLoading, stopLoading }}>
      {children}
      {count > 0 && <LoadingAcorn />}
    </LoadingContext.Provider>
  );
};
